// useHorizontalScrollbar.js
// macOS'ta native scrollbar overlay davranışı yüzünden yatay scrollbar
// çoğu zaman gizli kalıyor. Bu composable, container ile senkron çalışan
// her zaman görünür bir custom scrollbar (track + thumb) sağlar.

import { ref, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'

export function useHorizontalScrollbar() {
  const scrollEl = ref(null)
  const trackEl = ref(null)
  const thumbLeft = ref(0)
  const thumbWidth = ref(40)
  const trackVisible = ref(false)

  let dragging = false
  let dragStartX = 0
  let dragStartScrollLeft = 0
  let resizeObserver = null

  const updateThumb = () => {
    const s = scrollEl.value
    const t = trackEl.value
    if (!s || !t) return

    const trackW = t.clientWidth
    if (s.scrollWidth <= s.clientWidth) {
      trackVisible.value = false
      return
    }
    trackVisible.value = true

    const ratio = s.clientWidth / s.scrollWidth
    const width = Math.max(40, ratio * trackW)
    thumbWidth.value = width

    const maxScroll = s.scrollWidth - s.clientWidth
    const scrollRatio = maxScroll > 0 ? s.scrollLeft / maxScroll : 0
    thumbLeft.value = scrollRatio * (trackW - width)
  }

  const onScroll = () => updateThumb()

  const startDrag = (e) => {
    e.preventDefault()
    if (!scrollEl.value) return
    dragging = true
    dragStartX = e.clientX
    dragStartScrollLeft = scrollEl.value.scrollLeft
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onDrag)
    document.addEventListener('mouseup', stopDrag)
  }

  const onDrag = (e) => {
    if (!dragging) return
    const s = scrollEl.value
    const t = trackEl.value
    if (!s || !t) return
    const trackW = t.clientWidth
    const deltaX = e.clientX - dragStartX
    const maxScroll = s.scrollWidth - s.clientWidth
    const scrollableTrack = trackW - thumbWidth.value
    if (scrollableTrack <= 0) return
    const scrollDelta = (deltaX / scrollableTrack) * maxScroll
    s.scrollLeft = dragStartScrollLeft + scrollDelta
  }

  const stopDrag = () => {
    dragging = false
    document.body.style.userSelect = ''
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
  }

  const handleTrackClick = (e) => {
    // Sadece track'in kendisine tıklandıysa (thumb değil)
    if (e.target !== trackEl.value) return
    const s = scrollEl.value
    const t = trackEl.value
    if (!s || !t) return
    const rect = t.getBoundingClientRect()
    const clickX = e.clientX - rect.left
    const trackW = t.clientWidth
    const targetRatio = (clickX - thumbWidth.value / 2) / (trackW - thumbWidth.value)
    const clamped = Math.max(0, Math.min(1, targetRatio))
    s.scrollLeft = clamped * (s.scrollWidth - s.clientWidth)
  }

  onMounted(() => {
    nextTick(() => {
      if (scrollEl.value) {
        scrollEl.value.addEventListener('scroll', onScroll)
        // İçerik değişikliğini yakala
        if (typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => updateThumb())
          resizeObserver.observe(scrollEl.value)
        }
        updateThumb()
      }
      window.addEventListener('resize', updateThumb)
    })
  })

  onBeforeUnmount(() => {
    if (scrollEl.value) {
      scrollEl.value.removeEventListener('scroll', onScroll)
    }
    if (resizeObserver) {
      resizeObserver.disconnect()
    }
    window.removeEventListener('resize', updateThumb)
    document.removeEventListener('mousemove', onDrag)
    document.removeEventListener('mouseup', stopDrag)
    document.body.style.userSelect = ''
  })

  // Slot dizisi (props değişirken) update tetikle
  const refresh = () => nextTick(() => updateThumb())

  return {
    scrollEl,
    trackEl,
    thumbLeft,
    thumbWidth,
    trackVisible,
    startDrag,
    handleTrackClick,
    refresh
  }
}
